// Brick 15: Daily Growth Plan page — 30-day personalized habit tracker with streaks & check-ins
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, Sparkles, Loader2, Flame, CheckCircle2, Circle, Calendar, Trophy, Target,
  Brain, Compass, Users, FolderHeart, Lightbulb, Clock,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import WeKitLogo from "@/components/WeKitLogo";
import RoadmapPanel from "@/components/roadmap/RoadmapPanel";
import { isPreviewMode } from "@/lib/previewMode";
import { cloudStorage } from "@/lib/cloudStore";

interface Task {
  day: number; title: string; description: string; why: string;
  category: "skill" | "mindset" | "exploration" | "networking" | "portfolio" | "reflection";
  estimated_minutes: number;
}
interface WeeklyTheme { week: number; theme: string; focus: string; }
interface PlanContent { intro: string; weekly_themes: WeeklyTheme[]; tasks: Task[]; }
interface Plan { id: string; target_career: string; started_at: string; tasks: PlanContent; }
interface Checkin { day_number: number; mood: number | null; reflection: string | null; completed_on: string; }

const CATEGORY_META: Record<Task["category"], { icon: typeof Brain; color: string; label: string }> = {
  skill:       { icon: Brain,       color: "text-primary",           label: "Skill" },
  mindset:     { icon: Lightbulb,   color: "text-yellow-500",        label: "Mindset" },
  exploration: { icon: Compass,     color: "text-accent",            label: "Explore" },
  networking:  { icon: Users,       color: "text-blue-500",          label: "Connect" },
  portfolio:   { icon: FolderHeart, color: "text-pink-500",          label: "Portfolio" },
  reflection:  { icon: Sparkles,    color: "text-purple-500",        label: "Reflect" },
};

export default function GrowthPlan() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState<Plan | null>(null);
  const [checkins, setCheckins] = useState<Checkin[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [targetCareer, setTargetCareer] = useState("");
  const [activeWeek, setActiveWeek] = useState("1");
  const [reflectionFor, setReflectionFor] = useState<number | null>(null);
  const [reflectionText, setReflectionText] = useState("");
  const [reflectionMood, setReflectionMood] = useState(3);

  // Initial load
  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) {
        // Dev-only preview door: render the roadmap surface without a session.
        if (isPreviewMode()) { setLoading(false); return; }
        navigate("/auth");
        return;
      }

      const { data: p } = await supabase
        .from("growth_plans")
        .select("id, target_career, started_at, tasks")
        .eq("user_id", u.user.id).eq("is_active", true)
        .order("created_at", { ascending: false }).limit(1).maybeSingle();

      if (p) {
        setPlan(p as any);
        const { data: cs } = await supabase
          .from("growth_checkins")
          .select("day_number, mood, reflection, completed_on")
          .eq("plan_id", p.id);
        setCheckins((cs as Checkin[]) ?? []);
      } else {
        // Pre-fill from interview/resume career if available
        setTargetCareer(cloudStorage.getItem("wekit_interview_career") ?? "");
      }
      setLoading(false);
    })();
  }, [navigate]);

  const completedDays = useMemo(() => new Set(checkins.map((c) => c.day_number)), [checkins]);
  const totalDone = completedDays.size;
  const totalDays = plan?.tasks.tasks.length ?? 30;
  const percent = Math.round((totalDone / totalDays) * 100);

  // Streak: count consecutive completed days from day 1 forward, then from highest completed backward
  const streak = useMemo(() => {
    if (!plan) return 0;
    const sorted = [...completedDays].sort((a, b) => a - b);
    if (sorted.length === 0) return 0;
    let best = 1, cur = 1;
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === sorted[i - 1] + 1) { cur++; best = Math.max(best, cur); }
      else cur = 1;
    }
    return best;
  }, [completedDays, plan]);

  const generatePlan = async () => {
    if (!targetCareer.trim()) {
      toast({ title: "Pick a target career first" });
      return;
    }
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-growth-plan", {
        body: {
          target_career: targetCareer.trim(),
          assessment_id: cloudStorage.getItem("wekit_assessment_id") || undefined,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setPlan({
        id: data.plan_id, target_career: targetCareer.trim(),
        started_at: new Date().toISOString(),
        tasks: { intro: data.intro, weekly_themes: data.weekly_themes, tasks: data.tasks },
      });
      setCheckins([]);
      toast({ title: "Your 30-day plan is ready! 🚀" });
    } catch (e: any) {
      toast({ title: "Couldn't generate plan", description: e.message, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const completeDay = async (day: number) => {
    if (!plan) return;
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    const { error } = await supabase.from("growth_checkins").upsert({
      user_id: u.user.id, plan_id: plan.id, day_number: day,
      mood: reflectionFor === day ? reflectionMood : null,
      reflection: reflectionFor === day ? (reflectionText.trim() || null) : null,
    }, { onConflict: "plan_id,day_number" });
    if (error) { toast({ title: "Couldn't save", description: error.message, variant: "destructive" }); return; }
    setCheckins((p) => [...p.filter((c) => c.day_number !== day), {
      day_number: day,
      mood: reflectionFor === day ? reflectionMood : null,
      reflection: reflectionFor === day ? (reflectionText.trim() || null) : null,
      completed_on: new Date().toISOString().slice(0, 10),
    }]);
    if (reflectionFor === day) {
      setReflectionFor(null); setReflectionText(""); setReflectionMood(3);
    }
    toast({ title: `Day ${day} done! 🔥` });
  };

  const undoDay = async (day: number) => {
    if (!plan) return;
    await supabase.from("growth_checkins").delete().eq("plan_id", plan.id).eq("day_number", day);
    setCheckins((p) => p.filter((c) => c.day_number !== day));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container max-w-5xl flex items-center justify-between py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <WeKitLogo className="h-7" />
          <div className="w-16" />
        </div>
      </header>

      <main className="container max-w-5xl py-8 space-y-6">
        {/* Career DNA™ learning roadmap — tracks before tasks. */}
        <RoadmapPanel kind="learning" />

        {!plan ? (
          <Card className="border-border/50">
            <CardHeader>
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-gradient-to-br from-primary to-accent p-2.5">
                  <Target className="h-5 w-5 text-primary-foreground" />
                </div>
                <div>
                  <CardTitle className="font-display text-2xl">Start your 30-Day Growth Plan</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    YOMA designs a personalized daily plan tied to your traits and target career. 10-60 min/day.
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs font-medium">Target career *</label>
                <Input
                  value={targetCareer}
                  onChange={(e) => setTargetCareer(e.target.value)}
                  placeholder="e.g. Product Designer, Data Analyst, UX Researcher"
                  className="mt-1"
                />
              </div>
              <Button
                onClick={generatePlan}
                disabled={generating}
                size="lg"
                className="w-full rounded-full gap-2 bg-gradient-to-r from-primary to-accent text-primary-foreground"
              >
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {generating ? "Designing your 30 days…" : "Generate my plan"}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Top stats */}
            <div className="text-center space-y-2">
              <h1 className="font-display text-3xl sm:text-4xl font-bold gradient-text">
                30 Days to {plan.target_career}
              </h1>
              <p className="text-muted-foreground text-sm max-w-xl mx-auto">{plan.tasks.intro}</p>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <Stat icon={<CheckCircle2 className="h-5 w-5 text-emerald-500" />} label="Days complete" value={`${totalDone}/${totalDays}`} />
              <Stat icon={<Flame className="h-5 w-5 text-orange-500" />} label="Best streak" value={`${streak} days`} />
              <Stat icon={<Trophy className="h-5 w-5 text-yellow-500" />} label="Progress" value={`${percent}%`} />
            </div>
            <Progress value={percent} className="h-2" />

            {/* Weekly themes */}
            <Card className="border-border/50">
              <CardContent className="p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {plan.tasks.weekly_themes.map((w) => (
                  <button
                    key={w.week}
                    onClick={() => setActiveWeek(String(w.week))}
                    className={`text-left rounded-lg border p-3 transition ${
                      activeWeek === String(w.week)
                        ? "border-primary bg-primary/5"
                        : "border-border/40 hover:border-primary/40"
                    }`}
                  >
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Week {w.week}</p>
                    <p className="font-display font-semibold text-sm mt-0.5">{w.theme}</p>
                    <p className="text-xs text-muted-foreground mt-1">{w.focus}</p>
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* Daily tasks per week */}
            <Tabs value={activeWeek} onValueChange={setActiveWeek}>
              <TabsList className="grid w-full grid-cols-4">
                {[1, 2, 3, 4].map((w) => (
                  <TabsTrigger key={w} value={String(w)}>Week {w}</TabsTrigger>
                ))}
              </TabsList>
              {[1, 2, 3, 4].map((w) => {
                const start = (w - 1) * 7 + 1;
                const end = w === 4 ? 30 : w * 7;
                const days = plan.tasks.tasks.filter((t) => t.day >= start && t.day <= end);
                return (
                  <TabsContent key={w} value={String(w)} className="mt-4 space-y-3">
                    {days.map((t) => {
                      const done = completedDays.has(t.day);
                      const meta = CATEGORY_META[t.category];
                      const Icon = meta.icon;
                      return (
                        <Card key={t.day} className={`border-border/50 transition ${done ? "bg-emerald-500/5 border-emerald-500/30" : ""}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <button
                                onClick={() => done ? undoDay(t.day) : completeDay(t.day)}
                                className="shrink-0 mt-0.5"
                                aria-label={done ? "Mark incomplete" : "Mark complete"}
                              >
                                {done
                                  ? <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                                  : <Circle className="h-6 w-6 text-muted-foreground hover:text-primary transition" />}
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge variant="outline" className="text-[10px]">Day {t.day}</Badge>
                                  <Badge variant="outline" className={`text-[10px] ${meta.color} border-current/30`}>
                                    <Icon className="h-3 w-3 mr-1" />{meta.label}
                                  </Badge>
                                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                    <Clock className="h-3 w-3" />{t.estimated_minutes} min
                                  </span>
                                </div>
                                <h3 className="font-display font-semibold mt-1.5">{t.title}</h3>
                                <p className="text-sm text-foreground/85 mt-1">{t.description}</p>
                                <p className="text-xs italic text-primary/90 border-l-2 border-primary/30 pl-2 mt-2">{t.why}</p>

                                {!done && reflectionFor !== t.day && (
                                  <button
                                    onClick={() => { setReflectionFor(t.day); setReflectionText(""); setReflectionMood(3); }}
                                    className="text-xs text-primary hover:underline mt-2"
                                  >
                                    + Add a reflection on complete
                                  </button>
                                )}
                                {reflectionFor === t.day && (
                                  <div className="mt-3 space-y-2 rounded-lg border border-border/50 p-3 bg-card">
                                    <div className="flex items-center gap-2 text-xs">
                                      <span className="text-muted-foreground">Mood:</span>
                                      {[1, 2, 3, 4, 5].map((m) => (
                                        <button
                                          key={m}
                                          onClick={() => setReflectionMood(m)}
                                          className={`h-7 w-7 rounded-full text-sm ${reflectionMood === m ? "bg-primary text-primary-foreground" : "bg-muted"}`}
                                        >
                                          {["😞", "😐", "🙂", "😊", "🤩"][m - 1]}
                                        </button>
                                      ))}
                                    </div>
                                    <Textarea
                                      value={reflectionText}
                                      onChange={(e) => setReflectionText(e.target.value)}
                                      placeholder="What did you learn? Any wins or blockers?"
                                      rows={2}
                                    />
                                    <div className="flex gap-2 justify-end">
                                      <Button size="sm" variant="ghost" onClick={() => setReflectionFor(null)}>Cancel</Button>
                                      <Button size="sm" onClick={() => completeDay(t.day)}>Save & complete</Button>
                                    </div>
                                  </div>
                                )}

                                {done && (() => {
                                  const c = checkins.find((x) => x.day_number === t.day);
                                  if (!c?.reflection && !c?.mood) return null;
                                  return (
                                    <div className="mt-2 text-xs bg-muted/30 rounded-lg p-2 border border-border/40">
                                      {c.mood ? <span className="mr-2">{["😞", "😐", "🙂", "😊", "🤩"][c.mood - 1]}</span> : null}
                                      {c.reflection ? <span className="text-foreground/80">{c.reflection}</span> : null}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </TabsContent>
                );
              })}
            </Tabs>

            <Card className="border-border/50">
              <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" /> Started {new Date(plan.started_at).toLocaleDateString()}
                </div>
                <Button
                  variant="outline" size="sm"
                  onClick={() => { setPlan(null); setCheckins([]); setTargetCareer(plan.target_career); }}
                >
                  Generate a new plan
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Card className="border-border/50">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="rounded-lg bg-muted/50 p-2">{icon}</div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="font-display font-bold text-lg">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
